import CloseIcon from '@mui/icons-material/Close';
import Box from '@mui/material/Box';
import Dialog, { type DialogProps } from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import IconButton from '@mui/material/IconButton';
import { Breakpoint, SxProps, Theme } from '@mui/material/styles';
import Typography from '@mui/material/Typography';
import type {
	ComponentPropsWithRef,
	FormEventHandler,
	ReactNode,
	Ref,
	UIEvent
} from 'react';
import { useCallback, useLayoutEffect, useRef, useState } from 'react';

import { mergeSx } from '@/utils/mergeSx';

import { modalStyles as sx } from './Modal.styles';

const setRef = <T,>(ref: Ref<T> | null | undefined, node: T | null) => {
	if (typeof ref === 'function') {
		ref(node);
	} else if (ref) {
		(ref as { current: T | null }).current = node;
	}
};

export type Props = Omit<DialogProps, 'title' | 'onSubmit'> & {
	title: ReactNode;
	titleAdornment?: ReactNode;
	header?: ReactNode;
	showHeaderDivider?: boolean;
	children: ReactNode;
	footer?: ReactNode;
	footerSpacing?: boolean;
	size?: Breakpoint;
	width?: number;
	height?: number;
	fullHeight?: boolean;
	disableShadow?: boolean;
	disableFooterShadow?: boolean;
	disableHeaderShadow?: boolean;
	hideCloseButton?: boolean;
	onExited?: () => void;
	onSubmit?: FormEventHandler<HTMLFormElement>;
	bodyRef?: Ref<HTMLDivElement>;
	disableEscapeKeyDown?: boolean;
	customSx?: {
		body?: SxProps<Theme>;
		header?: SxProps<Theme>;
		footer?: SxProps<Theme>;
		titleRow?: SxProps<Theme>;
	};
};

export const Modal = ({
	open,
	onClose,
	title,
	titleAdornment,
	header,
	showHeaderDivider = false,
	children,
	footer,
	footerSpacing = true,
	hideCloseButton = false,
	size = 'sm',
	width,
	height,
	fullHeight = false,
	disableShadow = false,
	disableFooterShadow = false,
	disableHeaderShadow = false,
	onExited,
	onSubmit,
	bodyRef,
	disableEscapeKeyDown,
	customSx,
	...dialogProps
}: Props) => {
	const [scrolled, setScrolled] = useState(false);
	const [atBottom, setAtBottom] = useState(false);
	const internalBodyRef = useRef<HTMLDivElement | null>(null);

	const setBodyNode = useCallback(
		(node: HTMLDivElement | null) => {
			internalBodyRef.current = node;
			setRef(bodyRef, node);
		},
		[bodyRef]
	);

	useLayoutEffect(() => {
		const el = internalBodyRef.current;
		if (!el) return;

		setAtBottom(el.scrollHeight <= el.clientHeight + 1);
	}, [open, children]);

	const handleCloseClick = () => {
		if (onClose) {
			onClose({}, 'escapeKeyDown');
		}
	};

	const handleDialogClose: DialogProps['onClose'] = (event, reason) => {
		if (disableEscapeKeyDown && reason === 'escapeKeyDown') return;
		onClose?.(event, reason);
	};

	const handleBodyScroll = (event: UIEvent<HTMLDivElement>) => {
		const { scrollTop, scrollHeight, clientHeight } = event.currentTarget;
		setScrolled(scrollTop > 0);
		setAtBottom(scrollTop + clientHeight >= scrollHeight - 1);
	};

	const paperProps = onSubmit
		? ({
				component: 'form',
				onSubmit,
				noValidate: true,
				elevation: disableShadow ? 0 : undefined,
				sx: sx.paper(fullHeight, disableShadow, width, height)
			} as ComponentPropsWithRef<'form'> & { sx: SxProps<Theme> })
		: {
				elevation: disableShadow ? 0 : undefined,
				sx: sx.paper(fullHeight, disableShadow, width, height)
			};

	return (
		<Dialog
			open={open}
			onClose={handleDialogClose}
			fullWidth
			maxWidth={size}
			aria-labelledby='modal-title'
			aria-describedby='modal-description'
			slotProps={{ paper: paperProps, transition: { onExited } }}
			{...dialogProps}
		>
			<DialogTitle
				sx={mergeSx(
					sx.title,
					showHeaderDivider ? sx.titleDivider(scrolled) : null
				)}
				id='modal-title'
				component='div'
			>
				<Box sx={mergeSx(sx.titleRow, customSx?.titleRow)}>
					<Box sx={sx.titleSlot}>
						{typeof title === 'string' ? (
							<Typography variant='h6' component='h2' sx={sx.titleText}>
								{title}
							</Typography>
						) : (
							title
						)}
					</Box>
					{(titleAdornment || !hideCloseButton) && (
						<Box sx={sx.titleEnd}>
							{titleAdornment}
							{!hideCloseButton && (
								<IconButton
									onClick={handleCloseClick}
									aria-label='Close dialog'
								>
									<CloseIcon />
								</IconButton>
							)}
						</Box>
					)}
				</Box>
			</DialogTitle>

			{header && (
				<Box
					sx={mergeSx(
						sx.header(scrolled, disableHeaderShadow),
						customSx?.header
					)}
				>
					{header}
				</Box>
			)}

			<DialogContent
				ref={setBodyNode}
				onScroll={handleBodyScroll}
				sx={mergeSx(sx.body(!!footer), customSx?.body)}
				id='modal-description'
			>
				{children}
			</DialogContent>

			{footer && (
				<DialogActions
					sx={mergeSx(
						sx.footer(atBottom, footerSpacing, disableFooterShadow),
						customSx?.footer
					)}
				>
					{footer}
				</DialogActions>
			)}
		</Dialog>
	);
};
